import { InfoIcon } from "@imi/icons/InfoIcon.js";
import {
  ImageSkeleton,
  TextSkeleton,
} from "@imi/ui/components/Skeleton/Skeleton.js";
import { Tooltip } from "@imi/ui/components/Tooltip/Tooltip.js";
import { TooltipContent } from "@imi/ui/components/Tooltip/TooltipContent.js";
import { TooltipTrigger } from "@imi/ui/components/Tooltip/TooltipTrigger.js";
import { useParseValidationResult } from "@src/core/engine/validationResults/useParseValidationResult";
import { getProductImage } from "@src/core/products/images/getProductImage";
import productPlaceholder from "@src/shared/components/ProductDetailsCard/assets/product-placeholder.svg";
import { use } from "react";
import { Trans, useTranslation } from "react-i18next";
import { SuggestionAlternativesDialogContext } from "../../../contexts/SuggestionAlternativesDialogContext";
import { BatchSelectionRow } from "../../../store/types";
import styles from "./SuggestionName.module.css";

type SuggestionNameProps = {
  row: BatchSelectionRow;
};

export function SuggestionName({ row }: SuggestionNameProps) {
  const { t } = useTranslation("product");
  const { openDialog } = use(SuggestionAlternativesDialogContext);
  const parseValidationResult = useParseValidationResult({
    fieldsT: { prefix: "selection.batch.dataTable.status", t },
    maxDecimals: 3,
  });

  if (row.output.status === "loading") {
    return (
      <div className={styles.SuggestionContainer}>
        <ImageSkeleton />
        <TextSkeleton />
      </div>
    );
  }

  if (!row.output.suggestion) return null;
  const productImage = getProductImage(row.output.suggestion?.articleNumber);

  return (
    <div className={styles.SuggestionContainer}>
      <div className={styles.SuggestionImage}>
        <img
          alt=""
          src={productImage ?? productPlaceholder}
          width={productImage ? 30 : 20}
        />
      </div>
      <Tooltip disabled={row.status?.severity !== "info"}>
        <TooltipTrigger asChild tabIndex={undefined}>
          <a
            className={styles.SuggestionNameLink}
            href="#"
            onClick={() => {
              openDialog(row);
            }}
          >
            {row.output.suggestion?.name}
            {row.status?.severity === "info" && <InfoIcon />}
          </a>
        </TooltipTrigger>
        {row.status && (
          <TooltipContent
            className={styles.TooltipContent}
            variant={row.status.severity}
          >
            {row.status.code === "custom" ? (
              <Trans
                components={[<br />, <strong />]}
                i18nKey={`selection.batch.dataTable.status.${row.status.translationKey}`}
                ns="product"
                values={{
                  path: t(
                    `selection.batch.dataTable.status.fields.${row.status.path}`,
                  ),
                }}
              />
            ) : (
              parseValidationResult(row.status)
            )}
          </TooltipContent>
        )}
      </Tooltip>
    </div>
  );
}
